import { motion } from 'framer-motion';
import { ClipboardPaste, Zap, Rocket } from 'lucide-react';

const steps = [
  {
    number: '01',
    icon: ClipboardPaste,
    title: 'Paste your post',
    description: 'Drop in your blog URL, paste text, or upload a PDF. AdoriAI reads your content and understands what it\'s about in seconds.',
  },
  {
    number: '02',
    icon: Zap,
    title: 'AI builds your video',
    description: 'A script gets written, visuals are pulled from 100M+ assets, a voiceover is recorded, and captions are synced — all while you grab a coffee.',
  },
  {
    number: '03',
    icon: Rocket,
    title: 'Publish & grow',
    description: 'Tweak anything in the live editor, then export to YouTube, TikTok, Instagram Reels, or LinkedIn. Watch your audience finally find your work.',
  },
];

const HowItWorks = () => {
  return (
    <section id="how-it-works" className="py-24 px-6" data-testid="how-it-works-section">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-accent mb-4 block">
            How it works
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
            Blog post in. Viral video out.
            <br />
            <span className="text-gray-400">Three steps.</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            No timeline scrubbing. No Premiere Pro. No hiring anyone. Just you and your content.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              viewport={{ once: true }}
              className="relative"
            >
              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-16 left-full w-full h-0.5 bg-gradient-to-r from-brand-primary/50 to-transparent -translate-x-1/2 z-0"></div>
              )}
              
              <div className="relative z-10 bg-surface rounded-2xl p-8 border border-white/10 hover:border-brand-primary/30 transition-colors group">
                {/* Step Number */}
                <div className="flex items-center justify-between mb-6">
                  <span className="text-6xl font-heading font-black text-white/5 group-hover:text-brand-primary/10 transition-colors">
                    {step.number}
                  </span>
                  <div className="w-14 h-14 rounded-2xl bg-brand-primary/10 border border-brand-primary/20 flex items-center justify-center group-hover:bg-brand-primary/20 transition-colors">
                    <step.icon className="w-6 h-6 text-brand-primary" />
                  </div>
                </div>
                
                <h3 className="text-xl font-heading font-bold text-white mb-3">{step.title}</h3>
                <p className="text-gray-400 leading-relaxed">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
